import React, { useContext } from "react";
import { IntlContext, ListContext } from "context";
import { BookingInterface } from "hooks/booking";
import bookingValidationData, { BookingValidationStatus } from "hooks/bookingValidation";
import Button from "ui/Button";
import Modal from "ui/Modal";
import { Form } from "react-final-form";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { Role } from "types/user";
import { TextField } from "components/Form/Field";
import { getErrorResponseData, hasKey, isRecord } from "helpers/typing";
import { useNavigate } from "react-router";
import dataFetcher from "hooks/bookService";
import { onlineSalaryDeclaration } from "hooks/cesu";
import { buildRoute } from "routes";
import { toast } from "sonner";

interface ValidateServiceModalProps {
    isHiddenValidateServiceModal: boolean;
    setIsHiddenValidateServiceModal: (hidden: boolean) => void;
    booking: BookingInterface;
    dateFormatter: (date: string) => string;
    role: Role.CLIENT | Role.PROVIDER | undefined;
    startTimeSplit: string;
}

const ValidateBookingModal: React.FC<ValidateServiceModalProps> = ({
    isHiddenValidateServiceModal,
    setIsHiddenValidateServiceModal,
    booking,
    dateFormatter,
    role,
    startTimeSplit,
}) => {
    const intl = useContext(IntlContext);
    const { refreshData } = useContext(ListContext);
    const navigate = useNavigate();
    const hasTransmittingCesu = booking?.transmitting_cesu && Boolean(String(booking?.transmitting_cesu));

    let initialValues = {
        booking_id: booking.id,
        status: BookingValidationStatus.NON_VALIDATED,
        duration: booking.nb_hours.toString().padStart(2, "0") + ":00",
    };

    if (!role) {
        return <></>;
    }

    const onSubmit = async (data: Record<string, unknown>) => {
        try {
            const durationValue = (data as Record<string, unknown>).duration;
            const duration = String(durationValue).padStart(2, "0") + ":00";
            const bookingNbHours = booking.nb_hours.toString().padStart(2, "0") + ":00:00";
            const hasChangeDuration = duration !== bookingNbHours;

            if (Boolean(booking.transmitting_cesu) && role === Role.PROVIDER) {
                await bookingValidationData.create({
                    booking_id: booking.id,
                    status: hasChangeDuration ? BookingValidationStatus.DRAFT : BookingValidationStatus.VALIDATED,
                    duration: data.duration ?? booking.nb_hours,
                });

                if (!hasChangeDuration) {
                    toast.success(intl.formatMessage("booking.list.modal.validate_services.success"));
                    setIsHiddenValidateServiceModal(true);
                    refreshData();
                } else {
                    navigate(buildRoute.myReservationResume(booking.id));
                }
            }

            if (Boolean(booking.transmitting_cesu) && role === Role.CLIENT) {
                await onlineSalaryDeclaration(booking?.id).then(async () => {
                    await bookingValidationData.create({
                        booking_id: booking.id,
                        status: hasChangeDuration ? BookingValidationStatus.DRAFT : BookingValidationStatus.VALIDATED,
                        duration: data.duration ?? booking.nb_hours,
                    }).then(async () => {
                        await dataFetcher.capturePayment({
                            booking_id: booking.id,
                        });

                        if (!hasChangeDuration) {
                            toast.success(intl.formatMessage("our_services.booking.confirm.message.transmitting_cesu.success"));
                            setIsHiddenValidateServiceModal(true);
                            refreshData();
                        } else {
                            navigate(buildRoute.myReservationResume(booking.id));
                        }
                    });
                }).catch(() => {
                    toast.error(intl.formatMessage("our_services.booking.confirm.message.transmitting_cesu.error"));
                });
            }
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            let error = "";

            if (
                errData &&
                isRecord(errData.data) &&
                hasKey(errData.data, "error") &&
                isRecord(errData.data.error) &&
                typeof errData.data.error.description === "string"
            ) {
                const errDesc = JSON.parse(errData.data.error.description);
                error = errDesc.description;
            } else if (
                errData &&
                isRecord(errData.data) &&
                hasKey(errData.data, "data") &&
                isRecord(errData.data.data) &&
                hasKey(errData.data.data, "booking_id") &&
                isRecord(errData.data.data.booking_id) &&
                typeof errData.data.data.booking_id.message === "string"
            ) {
                error = errData.data.data.booking_id.message;
            }
            
            toast.error(intl.formatMessage("our_services.booking.confirm.message.transmitting_cesu.error")
            + " Raison : \"" + error + "\"");
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Modal
                onClose={() => setIsHiddenValidateServiceModal(true)}
                isHidden={isHiddenValidateServiceModal}
                title={intl.formatMessage("booking.list.modal.validate_services.title")}
                width={"medium"}
            >
                <Form
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    render={({ handleSubmit }) => {
                        return (
                            <form onSubmit={handleSubmit}>
                                <div className="flex flex--direction-column margin-bottom--5 flex--justify-content-center">
                                    <p className={hasTransmittingCesu ? `text-center` : ''}>
                                        {intl.formatMessage(`booking.list.modal.validate_services.content.${role}`, {
                                            subcategory: booking.subcategory ? intl.formatMessage(`subcategory.${booking.category.toString().toLowerCase()}.${booking.subcategory.toString().toLowerCase()}`) : intl.formatMessage(`service.label.category.${booking.category.toString()}`),
                                            firstname:
                                                role === Role.CLIENT
                                                    ? booking.provider.first_name
                                                    : booking.client.first_name,
                                            lastname:
                                                role === Role.CLIENT
                                                    ? booking.provider.last_name
                                                    : booking.client.last_name,
                                            date: dateFormatter(booking.date),
                                            start: startTimeSplit,
                                            end: parseInt(startTimeSplit) + booking.nb_hours,
                                        })}
                                    </p>

                                    {(role === Role.CLIENT && !hasTransmittingCesu) && (
                                        <div className="margin-top--4 margin-bottom--4">
                                            <TextField
                                                inputProps={{
                                                    name: "duration",
                                                    type: "time",
                                                    required: true,
                                                    maxLength: 255,
                                                    min: initialValues.duration,
                                                }}
                                                labelProps={{
                                                    label: intl.formatMessage(
                                                        "booking.list.modal.validate_services.input"
                                                    ),
                                                }}
                                            />
                                        </div>
                                    )}

                                    {role === Role.CLIENT && (
                                        <p className={hasTransmittingCesu ? `text-center` : ''}>
                                            Après validation, les données de la prestation seront automatiquement transmises à l'URSSAF.
                                        </p>
                                    )}

                                    <div className="modal__group-btn text-center padding-top--4">
                                        <Button className="btn btn--primary" type="submit">
                                            {intl.formatMessage("booking.list.modal.validate_services.valide_action")}
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        );
                    }}
                />
            </Modal>
        </React.Fragment>
    );
};

export default ValidateBookingModal;
